import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from './ui/collapsible';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from './ui/dropdown-menu';
import { Button } from './ui/button';
import { Tabs } from './ui/tabs';
import { ViewSwitcher } from './ViewSwitcher';
import { RichTextField } from './RichTextField';
import { RelatedEntityLink } from './RelatedEntityLink';
import { InlineEditableText } from './InlineEditableText';
import { TooltipProvider } from './ui/tooltip';
import type { MentionableEntity } from './RichTextField';
import type { Storyline } from './Storylines';
import type { Scene } from './Scenes';
import { ChevronDown, MoreVertical, Pencil, Plus } from 'lucide-react';
import { RotatingDeleteIcon } from './RotatingDeleteIcon';
import { cn } from '@/lib/utils';
import { RICH_TEXT_HTML_CLASS } from '../utils/rich-text-html';
import { useToast } from '../contexts/ToastContext';

type ScenesViewMode = 'table' | 'grid';

interface SceneRowProps {
  scene: Scene;
  viewMode: ScenesViewMode;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}

const SceneRow: React.FC<SceneRowProps> = ({ scene, viewMode, onEdit, onDelete, deleting }) => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);

  const content = scene.description && (
    <div
      className={`${RICH_TEXT_HTML_CLASS} px-3 pb-3 text-sm text-muted-foreground [&_p]:my-0`}
      dangerouslySetInnerHTML={{ __html: scene.description }}
    />
  );

  const menu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
          onClick={(e) => e.stopPropagation()}
          aria-label={t('actions')}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onEdit} disabled={deleting}>
          <Pencil className="mr-2 h-4 w-4" />
          {t('edit')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onDelete} disabled={deleting} className="group text-red-600">
          <RotatingDeleteIcon className="mr-2" />
          {t('delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (viewMode === 'grid') {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onEdit}
        onKeyDown={(e) => e.key === 'Enter' && onEdit()}
        className="cursor-pointer rounded-lg border border-border bg-muted p-5 transition-colors hover:bg-card hover:shadow-sm"
      >
        <div className="flex items-start justify-between gap-2">
          <h4 className="min-w-0 flex-1 text-sm font-semibold text-card-foreground">{scene.title}</h4>
          {menu}
        </div>
        {scene.description && (
          <div
            className={`${RICH_TEXT_HTML_CLASS} mt-1 line-clamp-[7] text-sm text-muted-foreground [&_p]:my-0`}
            dangerouslySetInnerHTML={{ __html: scene.description }}
          />
        )}
      </div>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="border-b border-border bg-muted transition-colors last:border-b-0 hover:bg-card" onDoubleClick={onEdit}>
      <div className="flex items-center gap-2 py-2.5 pl-4 pr-2">
        <CollapsibleTrigger asChild>
          <button type="button" className="flex min-w-0 flex-1 items-center gap-1.5 text-left">
            <ChevronDown
              className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200', !open && '-rotate-90')}
            />
            <span className="truncate text-sm font-medium text-foreground">{scene.title}</span>
          </button>
        </CollapsibleTrigger>
        {menu}
      </div>
      <CollapsibleContent>{content}</CollapsibleContent>
    </Collapsible>
  );
};

interface StorylinePanelProps {
  storyline: Storyline;
  scenes: Scene[];
  mentionableEntities: MentionableEntity[];
  projectId?: string;
  documentId?: string;
  onEditStoryline: () => void;
  onDeleteStoryline: () => void;
  onTitleChange: (title: string) => void | boolean | Promise<void | boolean>;
  onDescriptionChange: (description: string) => void;
  onAddScene: () => void;
  onEditScene: (scene: Scene) => void;
  onDeleteScene: (scene: Scene) => void;
  deletingSceneId: string | null;
}

export const StorylinePanel: React.FC<StorylinePanelProps> = ({
  storyline,
  scenes,
  mentionableEntities,
  projectId,
  documentId,
  onEditStoryline,
  onDeleteStoryline,
  onTitleChange,
  onDescriptionChange,
  onAddScene,
  onEditScene,
  onDeleteScene,
  deletingSceneId,
}) => {
  const { t } = useLocale();
  const { showFieldSavedToast } = useToast();
  const [open, setOpen] = useState(false);
  const [scenesViewMode, setScenesViewMode] = useState<ScenesViewMode>('grid');
  const [description, setDescription] = useState(storyline.description ?? '');
  const latestDescriptionRef = useRef(description);
  const lastPersistedRef = useRef(storyline.description ?? '');
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onDescriptionChangeRef = useRef(onDescriptionChange);
  onDescriptionChangeRef.current = onDescriptionChange;

  const persistDescription = useCallback((html: string): void => {
    if (html === lastPersistedRef.current) return;
    lastPersistedRef.current = html;
    onDescriptionChangeRef.current(html);
    showFieldSavedToast(t('description'));
  }, [showFieldSavedToast, t]);

  useEffect(() => {
    const incoming = storyline.description ?? '';
    if (incoming === lastPersistedRef.current) return;
    setDescription(incoming);
    latestDescriptionRef.current = incoming;
    lastPersistedRef.current = incoming;
  }, [storyline.description]);

  useEffect(() => {
    if (open) return;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    persistDescription(latestDescriptionRef.current);
  }, [open, persistDescription]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        persistDescription(latestDescriptionRef.current);
      }
    };
  }, [persistDescription]);

  const handleDescriptionChange = (html: string): void => {
    setDescription(html);
    latestDescriptionRef.current = html;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      persistDescription(html);
      saveTimeoutRef.current = null;
    }, 500);
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-lg border border-border bg-card">
      <div
        className="flex cursor-pointer items-start justify-between gap-2 p-6"
        onClick={() => setOpen((isOpen) => !isOpen)}
      >
        <div className="flex min-w-0 flex-1 items-start gap-1.5">
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 p-0 text-muted-foreground hover:text-foreground"
              aria-label={t('close')}
              onClick={(e) => e.stopPropagation()}
            >
              <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', open && 'rotate-180')} />
            </Button>
          </CollapsibleTrigger>
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <TooltipProvider>
              <InlineEditableText
                value={storyline.title}
                onCommit={onTitleChange}
                savedFieldLabel={t('title')}
                as="h3"
                stopClickPropagation
                className="w-fit max-w-full text-lg font-semibold text-card-foreground"
              />
            </TooltipProvider>
            {!open && scenes.length > 0 && projectId && documentId && (
              <div
                className="flex flex-wrap gap-1.5"
                onClick={(e) => e.stopPropagation()}
              >
                {scenes.map((scene) => (
                  <RelatedEntityLink
                    key={scene.id}
                    projectId={projectId}
                    entity={{
                      type: 'scene',
                      id: scene.id,
                      documentId,
                      label: scene.title,
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                aria-label={t('actions')}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEditStoryline}>
                <Pencil className="mr-2 h-4 w-4" />
                {t('edit')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDeleteStoryline} className="group text-red-600">
                <RotatingDeleteIcon className="mr-2" />
                {t('delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <CollapsibleContent>
        <div className="px-6 pb-6">
          <RichTextField
            value={description}
            onChange={handleDescriptionChange}
            placeholder={t('storyline_description_placeholder')}
            minHeight="80px"
            mentionableEntities={mentionableEntities}
          />

          <div className="my-8 border-t border-border" />

          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-foreground">{t('scenes')}</span>
            <Tabs value={scenesViewMode} onValueChange={(v) => setScenesViewMode(v as ScenesViewMode)}>
              <ViewSwitcher
                modes={['table', 'grid']}
                ariaLabelForMode={(mode) => t(mode)}
                listClassName="h-6 gap-0.5 rounded-md border-none bg-transparent p-0.5"
                triggerClassName="h-5 w-5 text-muted-foreground hover:bg-muted hover:text-foreground aria-[selected=true]:bg-muted aria-[selected=true]:text-foreground aria-[selected=true]:shadow-none [&_svg]:h-3 [&_svg]:w-3"
                showTooltips={false}
              />
            </Tabs>
          </div>

          {scenesViewMode === 'grid' ? (
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
              {scenes.map((scene) => (
                <SceneRow
                  key={scene.id}
                  scene={scene}
                  viewMode="grid"
                  onEdit={() => onEditScene(scene)}
                  onDelete={() => onDeleteScene(scene)}
                  deleting={deletingSceneId === scene.id}
                />
              ))}
              <button
                type="button"
                onClick={onAddScene}
                className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-border p-5 text-sm font-medium text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
              >
                <Plus className="h-4 w-4" />
                {t('add_or_link_scene')}
              </button>
            </div>
          ) : (
            <>
              {scenes.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-border">
                  {scenes.map((scene) => (
                    <SceneRow
                      key={scene.id}
                      scene={scene}
                      viewMode="table"
                      onEdit={() => onEditScene(scene)}
                      onDelete={() => onDeleteScene(scene)}
                      deleting={deletingSceneId === scene.id}
                    />
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={onAddScene}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
              >
                <Plus className="h-4 w-4" />
                {t('add_or_link_scene')}
              </button>
            </>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
